import { Category, Languages, ProductApi } from 'common/types';
import { getTranslation } from 'common/modules/translations';
import { getActiveCategories } from 'common/utils/newRentalUtils';
import { getCategoryProducts } from 'common/utils/productUtils';
import { stringNormalize } from 'common/utils/strings';

import { FilterOptionsState } from '@mui/material/useAutocomplete';

export interface Option {
	id: string;
	label: string;
	categoryId: string;
	categoryLabel: string;
	selected: boolean;
}

export const getOptionsForActiveCategories = (
	categories: Category[],
	stockProducts: ProductApi[],
	lang: Languages,
): Option[] => {
	const activeCategories = getActiveCategories(categories, stockProducts);
	return activeCategories.reduce((options: Option[], category) => {
		const categoryProductOptions: Option[] = getCategoryProducts(stockProducts, category.id).map(
			(product) => ({
				id: product.id,
				label: getTranslation(product.name, lang),
				categoryId: category.id,
				categoryLabel: getTranslation(category.name, lang),
				selected: false,
			}),
		);
		return [...options, ...categoryProductOptions];
	}, []);
};

export const getOptionsForProducts = (value: ProductApi[], options: Option[]): Option[] =>
	value.flatMap((v) => options.filter((option) => option.id === v.id));

export const getOptionsForCategory = (options: Option[], categoryId: string): Option[] =>
	options.filter((option) => option.categoryId === categoryId);

export const filterOptions = (
	options: Option[],
	{ inputValue, getOptionLabel }: FilterOptionsState<Option>,
) => {
	const input = stringNormalize(inputValue);
	const filteredOptions = options.filter(
		(option) => stringNormalize(getOptionLabel(option)).indexOf(input) > -1,
	);
	return filteredOptions;
};

export const getProductsFromOptions = (value: ProductApi[], options: Option[]): ProductApi[] =>
	options.reduce((result: ProductApi[], option) => {
		const product = value.find((p) => p.id === option.id);
		if (product && !result.find((p) => p.id === product.id)) {
			result.push(product);
		}
		return result;
	}, []);

export const getOptionLabel = (option: Option) => option.label;

export const getOptionCategory = (option: Option) => option.categoryId;
